import type { CalendarClient } from '@wtfalch/calendar/calendar';
import { Button, Icon } from '@wtfalch/design';
import { useEffect, useState } from 'react';

import type { ComposeAnswering } from './Compose.tsx';

const LOOKAHEAD_MS = 3 * 24 * 3_600_000;
const SLOT = 'PT30M';

const slotLabel = (start: Date) =>
  `${start.toLocaleDateString(undefined, { weekday: 'short' })} ${start.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;

/** The first half hour in the next three days that `email` is free in, or
 *  `null`. Not shared, unknown, or failing to load all read as `null`: this
 *  is a hint, and a missing hint is not an error. */
function useFreeSlot(calendar: CalendarClient | undefined, email: string | null): Date | null {
  const [slot, setSlot] = useState<Date | null>(null);
  useEffect(() => {
    setSlot(null);
    if (!calendar || !email) return;
    let live = true;
    (async () => {
      const principal = await calendar.resolvePrincipal(email);
      if (!principal) return;
      const now = new Date();
      const found = await calendar.findTime(
        [principal.accountId],
        { after: now.toISOString(), before: new Date(now.getTime() + LOOKAHEAD_MS).toISOString() },
        SLOT,
        { workingHours: true },
      );
      const first = found.slots[0];
      const start = first ? new Date(first.after) : null;
      if (live && found.unknown.length === 0 && start && !Number.isNaN(start.getTime())) {
        setSlot(start);
      }
    })().catch((err: unknown) => {
      console.warn('Compose: could not look up a free time', err);
    });
    return () => {
      live = false;
    };
  }, [calendar, email]);
  return slot;
}

/**
 * The board's right-hand column while a mail is written: a note on what
 * kind of message this is, and "While you write" -- the thread this answers
 * and a time the first colleague on the list is free, to offer instead of
 * asking.
 */
export function ComposeAside({
  answering,
  calendarClient,
  colleague,
  colleagueName,
  onOfferTime,
}: {
  answering?: ComposeAnswering;
  calendarClient?: CalendarClient;
  /** The first colleague in To, or `null`. */
  colleague: string | null;
  colleagueName: string;
  onOfferTime: (label: string) => void;
}) {
  const slot = useFreeSlot(calendarClient, colleague);
  const rows = Boolean(answering) || slot !== null;
  return (
    <aside className="flex flex-col gap-4 mail-compose-aside" aria-label="While you write">
      <div className="card tint mail-compose-note">
        <span className="text-sm font-strong">This one is going as mail</span>
        <span className="text-xs text-muted">
          Mail keeps a record beside the thread. Switch to Message to reach someone in the moment.
        </span>
      </div>
      {rows && (
        <div className="flex flex-col gap-3">
          <span className="mail-folder-group-label">While you write</span>
          {answering && (
            <div className="flex items-center gap-3">
              <Icon name="mail" size={16} />
              <span className="flex flex-col grow min-w-0">
                <span className="text-sm truncate">The thread this answers</span>
                <span className="text-xs text-muted truncate">
                  {`${answering.from} · ${answering.count} message${answering.count === 1 ? '' : 's'}`}
                </span>
              </span>
            </div>
          )}
          {slot && (
            <div className="flex items-center gap-3">
              <Icon name="calendar" size={16} />
              <span className="flex flex-col grow min-w-0">
                <span className="text-sm truncate">{`${colleagueName} is free ${slotLabel(slot)}`}</span>
                <span className="text-xs text-muted">Offer a time instead of asking</span>
              </span>
              <Button size="sm" onPress={() => onOfferTime(slotLabel(slot))}>
                Offer
              </Button>
            </div>
          )}
        </div>
      )}
    </aside>
  );
}
